<template>
  <div class="departments-area">
    <ul class="search-box">
      <DepartmentsSearchBox
        :localized="localized"
        :disabled="disabled"
        :next-focus-holder="nextFocusHolder"
        :keyword="keyword"
        @focusNext="focusNextFromSearchBox"
        @focusPrev="focusPrevFromSearchBox"
        @change="onChangeDepartmentsKeyword"
        @blur="onBlur"
      />
    </ul>
    <div
      v-show="isTreeVisible"
      ref="tree"
      class="departments-tree"
      :class="{ 'small-margin': useSmallMargin }"
      @scroll="onScroll"
    >
      <ul v-if="isNotFiltered" class="list-group list-for-not-filtered">
        <div v-for="department in departments" :key="department.departmentIdStr">
          <DepartmentsTreeNode
            v-show="department.visibled"
            :department="department"
            :selected-department-id-str="selectedDepartmentIdStr"
            :next-focus-holder="nextFocusHolder"
            :denyIdList="denyIdList"
            @expandDepartment="expandDepartment"
            @collapseDepartment="collapseDepartment"
            @selectDepartment="selectDepartment"
            @blur="onBlur"
          />
        </div>
      </ul>
      <ul v-else class="list-group list-for-filtered">
        <FilteredDepartmentsTreeNode
          v-for="(filteredDepartment, index) in filteredDepartments"
          :key="filteredDepartment.departmentIdStr"
          :filtered-department="filteredDepartment"
          :next-focus-holder="nextFocusHolder"
          :keyword="keyword"
          :selected-department-id-str="selectedDepartmentIdStr"
          :denyIdList="denyIdList"
          @selectDepartment="selectDepartment"
          @focusNext="focusNext(index)"
          @focusPrev="focusPrev(index)"
          @blur="onBlur"
        />
      </ul>
    </div>
  </div>
</template>

<script lang="ts">
import type { Department } from "./values/Department";
import type { FilteredDepartment } from "./values/FilteredDepartment";
import DepartmentsTreeNode from "./DepartmentsTreeNode.vue";
import FilteredDepartmentsTreeNode from "./FilteredDepartmentsTreeNode.vue";
import DepartmentsSearchBox from "./DepartmentsSearchBox.vue";
import { computed, defineComponent, onBeforeUnmount, onMounted, ref, type PropType } from "vue";

export default defineComponent({
  name: "DepartmentsArea",
  components: {
    DepartmentsSearchBox,
    DepartmentsTreeNode,
    FilteredDepartmentsTreeNode,
  },
  props: {
    localized: { type: Boolean, required: true },
    disabled: { type: Boolean, required: true },
    departments: { type: Array as PropType<Department[]>, required: true },
    filteredDepartments: {
      type: Array as PropType<FilteredDepartment[]>,
      required: true,
    },
    selectedDepartmentIdStr: { type: String, required: true },
    useSmallMargin: { type: Boolean, default: false },
    denyIdList: { type: Array as PropType<string[]>, default: () => [] },
  },
  setup(props, { emit }) {
    const keyword = ref("");
    const nextFocusHolder = ref("");
    // NOTE: 旧コードでは data として宣言されたまま一度も代入されず常に null だった
    // （テンプレートの ref="tree" は this.$refs.tree にのみ入り、この data には入らない）。
    // setup() の return に含めると Vue 2.7 ではテンプレート ref として要素が束縛され
    // onWindowResize の挙動が変わってしまうため、振る舞い不変のためあえて return しない。
    const tree = ref<HTMLDivElement | null>(null);
    const treeHeight = ref(0);

    const visibleDepartments = computed((): Department[] => {
      return props.departments.filter((department) => department.visibled);
    });

    const isTreeVisible = computed((): boolean => {
      return props.departments.length !== 0 || props.filteredDepartments.length !== 0;
    });

    const isNotFiltered = computed((): boolean => {
      return keyword.value.length === 0;
    });

    // methods
    const expandDepartment = (departmentIdStr: string): void => {
      emit("expandDepartment", departmentIdStr);
    };

    const collapseDepartment = (departmentIdStr: string): void => {
      emit("collapseDepartment", departmentIdStr);
    };

    const selectDepartment = (departmentIdStr: string): void => {
      emit("selectDepartment", departmentIdStr);
    };

    const onChangeDepartmentsKeyword = (value: string): void => {
      keyword.value = value;
      emit("changeDepartmentsKeyword", value);
    };

    const onScroll = (): void => {
      emit("requestToLoadUserCount");
    };

    const onWindowResize = (): void => {
      const newTreeHeight = tree.value?.clientHeight ?? 0;
      if (treeHeight.value === newTreeHeight) {
        return;
      }
      emit("requestToLoadUserCount");
    };

    const getFocusHolderForNormalNodes = (newIndex: number): string | undefined => {
      if (newIndex < 0 || visibleDepartments.value.length - 1 < newIndex) {
        return undefined;
      }
      return visibleDepartments.value[newIndex]?.departmentIdStr;
    };

    const getFocusHolderForFilteredNodes = (newIndex: number): string | undefined => {
      if (newIndex < 0 || props.filteredDepartments.length - 1 < newIndex) {
        return undefined;
      }
      return props.filteredDepartments[newIndex]?.departmentIdStr;
    };

    const updateFocusHolder = (newIndex: number): void => {
      const result = isNotFiltered.value
        ? getFocusHolderForNormalNodes(newIndex)
        : getFocusHolderForFilteredNodes(newIndex);
      nextFocusHolder.value = result ?? "searchBox";
    };

    const focusNextFromSearchBox = (): void => {
      updateFocusHolder(0);
    };

    const focusPrevFromSearchBox = (): void => {
      const array = isNotFiltered.value ? visibleDepartments.value : props.filteredDepartments;
      updateFocusHolder(array.length - 1);
    };

    const focusPrev = (currentIndex: number): void => {
      updateFocusHolder(currentIndex - 1);
    };

    const focusNext = (currentIndex: number): void => {
      updateFocusHolder(currentIndex + 1);
    };

    const onBlur = () => {
      nextFocusHolder.value = "";
    };

    const clearDepartmentsKeyword = () => {
      keyword.value = "";
      emit("changeDepartmentsKeyword", "");
    };

    // lifecycle
    onMounted(() => {
      window.addEventListener("resize", onWindowResize);
    });

    // NOTE: 旧コードの beforeUnmount は Vue 2 のクラスコンポーネントではライフサイクル
    // フックとして認識されず（Vue 2 のフック名は beforeDestroy。vue-class-component では
    // 単なる methods 扱いで呼び出し元なし）、resize リスナーが解除されない既存バグがあった。
    // AttachmentImg.vue（PR-1）の beforeDestory 対応と同様、クラス → Composition API 変換の
    // このタイミングで onBeforeUnmount により正しく解除する（解除されなくても onWindowResize
    // は tree が常に null のため早期 return する no-op だが、リーク防止のため修正）。
    onBeforeUnmount(() => {
      window.removeEventListener("resize", onWindowResize);
    });

    return {
      keyword,
      nextFocusHolder,
      treeHeight,
      visibleDepartments,
      isTreeVisible,
      isNotFiltered,
      expandDepartment,
      collapseDepartment,
      selectDepartment,
      onChangeDepartmentsKeyword,
      onScroll,
      onWindowResize,
      getFocusHolderForNormalNodes,
      getFocusHolderForFilteredNodes,
      updateFocusHolder,
      focusNextFromSearchBox,
      focusPrevFromSearchBox,
      focusPrev,
      focusNext,
      onBlur,
      clearDepartmentsKeyword,
    };
  },
});
// 要注意: Haxe 経由での JQuery からのDOM変更が残っている
</script>

<style scoped lang="scss">
.departments-area {
  display: flex;
  flex-direction: column;

  .search-box {
    padding-left: 5%;
    padding-right: 5%;
  }

  > .departments-tree {
    position: relative;
    overflow-y: auto;
    flex-grow: 1;
    flex-shrink: 1;

    &.small-margin {
      margin-top: 10px;
    }
  }
}
</style>
